/**
 * Stage Chatbot-1/4:チャットボット設定ページ(/chatbot)。
 *
 * Phase 1+4 範囲:
 * - 基本字段读 / 写(3 字段):name / welcome_message / fallback_message
 * - Phase 4:placeholder / theme_color / title / allowed_domains / is_active
 *   5 个新增字段(合并到同一 PATCH /settings 走,handler 同 endpoint)
 * - 走真实 /api/chatbot/settings + /api/chatbot/widget API,绝无 mock fallback
 * - 4 状态:loading / ready / saving / error
 *
 * 不做(Phase 4.5+):
 * - widget_key rotate / 重新生成(占位按钮)
 * - 联系表单设置(Phase 5)— UI 不展示,但 PATCH 仍带上既有值保持兼容
 * - 嵌入预览 iframe(Phase 4.5)
 */

import { useEffect, useState } from "react";
import { Save, AlertCircle, CheckCircle2, Copy, Eye, EyeOff, Plus } from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { ApiError, apiFetch } from "../../api/client";
import { listMedia, MediaItem } from "../media/api/mediaApi";
import { createWidget, getWidget, rotateWidgetKey } from "./api/chatbotApi";
import { ChatbotAuditTab } from "./ChatbotAuditTab";
import { ChatbotKnowledgeTab } from "./ChatbotKnowledgeTab";
import { ChatbotSettings as ChatbotSettingsBase } from "./api/chatbotApi";

// ============================================================
// Types — 与 backend services/chatbot_widget_service 同步
// ============================================================

export type ChatbotSettings = ChatbotSettingsBase;

// 4 状态机:loading → ready / error; saving ⊂ ready
type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: ChatbotSettings; saving: boolean; saved: boolean };

type Tab = "settings" | "knowledge" | "audit";

const SESSION_STORAGE_KEY = "cb_admin_widget_key_one_time";

const emptySettings: ChatbotSettings = {
  id: "",
  tenant_id: "",
  name: "サポートボット",
  welcome_message: "こんにちは。ご質問を入力してください。",
  fallback_message: "お答えできる資料が見つかりませんでした。別の言い方で質問するか、管理者にお問い合わせください。",
  fallback_image_url: "",
  show_contact_form: true,
  notification_email: "",
  placeholder: "メッセージを入力...",
  theme_color: "#1e40af",
  title: "",
  allowed_domains: ["localhost", "127.0.0.1"],
  is_active: true,
};

export const ChatbotSettingsPage = () => {
  const [tab, setTab] = useState<Tab>("settings");
  const [state, setState] = useState<State>({ kind: "loading" });
  // Phase 4: 临时一次性 widget_key(本 sessionStorage 内有效,刷新即失)
  const [oneTimeKey, setOneTimeKey] = useState<string | null>(
    () => sessionStorage.getItem(SESSION_STORAGE_KEY) || null
  );
  const [showKey, setShowKey] = useState<boolean>(false);
  // Phase 4.11: widget_key_masked(永久显示)+ is_active 显示
  // 用 GET /api/chatbot/widget 拿 masked,创建后拿一次性明文
  const [widgetKeyMasked, setWidgetKeyMasked] = useState<string | null>(null);
  const [widgetExists, setWidgetExists] = useState<boolean>(false);
  const [creatingWidget, setCreatingWidget] = useState<boolean>(false);
  const [mediaImages, setMediaImages] = useState<MediaItem[]>([]);
  const [loadingMediaImages, setLoadingMediaImages] = useState<boolean>(false);

  // ----------------------------------------------------------
  // 初期 GET settings + widget meta
  // ----------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch<ChatbotSettings>("/chatbot/settings");
        if (cancelled) return;
        setState({ kind: "ready", data: res, saving: false, saved: false });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setWidgetExists(false);
          setState({ kind: "ready", data: emptySettings, saving: false, saved: false });
          return;
        }
        const msg =
          err instanceof Error ? err.message : "設定の読み込みに失敗しました。";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingMediaImages(true);
    (async () => {
      try {
        const res = await listMedia({ page: 1, per_page: 12, category: "image" });
        if (!cancelled) {
          setMediaImages(res.items.filter((item) => item.source_url || item.thumbnail_url));
        }
      } catch {
        if (!cancelled) setMediaImages([]);
      } finally {
        if (!cancelled) setLoadingMediaImages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Phase 4.11: 拿 widget metadata(masked key + 创建状态)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const w = await getWidget();
        if (cancelled) return;
        // 后端 GET /api/chatbot/widget 不返 widget_key,只返 settings;
        // 在 service 层 widget_key_masked 已经预留字段(return None)。
        // 我们这里要暴露 masked,从 widget_key 哈希派生不可行(server 没存原文),
        // 真实方案:从 widget_id 派生显示 placeholder "widget:<id 末 4>...<id 末 4>"
        // 让 admin 视觉识别 + 强提示可以再作成(走 createWidget 拿到新 plain)。
        if (w && w.id) {
          setWidgetExists(true);
          setWidgetKeyMasked(`widget_${w.id.slice(0, 4)}***${w.id.slice(-4)}`);
        }
      } catch (err) {
        if (cancelled) return;
        // 不算 fatal — settings page 仍可用
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Phase 4.11: 创建 widget
  const handleCreateWidget = async () => {
    if (creatingWidget) return;
    setCreatingWidget(true);
    try {
      const result = await createWidget("サポートボット");
      // result 包含 widget_key_plain_one_time + widget_key_masked
      const plain = result.widget_key_plain_one_time;
      const masked = result.widget_key_masked;
      if (plain) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, plain);
        setOneTimeKey(plain);
      }
      if (masked) {
        setWidgetKeyMasked(masked);
      }
      setWidgetExists(true);
      window.alert("Widget を作成しました。下の「Widget Key 表示切替」でキーを控えてください。\n\n※ このブラウザをリロードすると再度表示できません。");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Widget の作成に失敗しました。";
      window.alert(msg);
    } finally {
      setCreatingWidget(false);
    }
  };

  // Phase 4.12: rotate widget_key(旧失效 → 新明文一次性)
  const [rotating, setRotating] = useState<boolean>(false);
  const handleRotateKey = async () => {
    const ok = window.confirm(
      "Widget Key を再生成します。\n\n" +
        "⚠ 既に WordPress に埋め込んだ <script> の data-widget-key は\n" +
        "古い値のままだと、チャットボットが表示されなくなります。\n\n" +
        "続行しますか？",
    );
    if (!ok) return;
    setRotating(true);
    try {
      const result = await rotateWidgetKey();
      const plain = result.widget_key_plain_one_time;
      const masked = result.widget_key_masked;
      if (plain) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, plain);
        setOneTimeKey(plain);
      }
      if (masked) {
        setWidgetKeyMasked(masked);
      }
      window.alert("Widget Key を再生成しました。下の「Widget Key 表示切替」で新しいキーを控えてください。\n\n※ リロード前に必ずコピーしてください。");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Widget Key の再生成に失敗しました。";
      window.alert(msg);
    } finally {
      setRotating(false);
    }
  };

  // ----------------------------------------------------------
  // フォーム変更 → ローカル state のみ更新(server PATCH は save 時)
  // ----------------------------------------------------------
  const updateField = <K extends keyof ChatbotSettings>(
    key: K,
    value: ChatbotSettings[K]
  ) => {
    setState((prev) => {
      if (prev.kind !== "ready") return prev;
      return {
        ...prev,
        data: { ...prev.data, [key]: value },
        saved: false,
      };
    });
  };

  // ----------------------------------------------------------
  // 保存
  // ----------------------------------------------------------
  const handleSave = async () => {
    if (state.kind !== "ready") return;
    setState((prev) => (prev.kind === "ready" ? { ...prev, saving: true } : prev));
    try {
      const payload: Partial<ChatbotSettings> = {
        name: state.data.name,
        welcome_message: state.data.welcome_message,
        fallback_message: state.data.fallback_message,
        fallback_image_url: state.data.fallback_image_url,
        show_contact_form: state.data.show_contact_form,
        notification_email: state.data.notification_email,
        // Phase 4
        placeholder: state.data.placeholder,
        theme_color: state.data.theme_color,
        title: state.data.title,
        allowed_domains: state.data.allowed_domains,
        is_active: state.data.is_active,
      };
      const res = await apiFetch<ChatbotSettings>("/chatbot/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setState({ kind: "ready", data: res, saving: false, saved: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "保存に失敗しました。";
      setState((prev) =>
        prev.kind === "ready"
          ? { ...prev, saving: false, saved: false }
          : prev
      );
      window.alert(msg);
    }
  };

  // ----------------------------------------------------------
  // 埋め込みコード コピー
  // ----------------------------------------------------------
  const handleCopyEmbed = (widgetKeyPlain: string) => {
    const base = window.location.origin;
    const snippet = `<script src="${base}/static/chatbot/widget.js" data-widget-key="${widgetKeyPlain}" async></script>`;
    navigator.clipboard
      .writeText(snippet)
      .then(() => window.alert("埋め込みコードをコピーしました"))
      .catch(() => window.alert("クリップボードへのアクセスが拒否されました"));
  };

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
  if (state.kind === "loading") {
    return (
      <div className="page-loading">
        <p>読み込み中...</p>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <Card title="設定を読み込めません">
        <div className="error-state">
          <AlertCircle size={20} color="#dc2626" />
          <p>{state.message}</p>
          <Button
            onClick={() => {
              setState({ kind: "loading" });
              window.location.reload();
            }}
          >
            再読み込み
          </Button>
        </div>
      </Card>
    );
  }

  const { data, saving, saved } = state;

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
        チャットボット設定
      </h1>
      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 18 }}>
        WordPress サイトに埋め込むチャットボットの表示と挙動を設定します。
      </p>

      {/* Tab bar */}
      <div
        style={{
          display: "flex",
          gap: 0,
          borderBottom: "1px solid #e5e7eb",
          marginBottom: 18,
        }}
      >
        {(
          [
            { key: "settings", label: "基本設定" },
            { key: "knowledge", label: "知識ベース" },
            { key: "audit", label: "会話監査" },
          ] as { key: Tab; label: string }[]
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              color: tab === t.key ? "#1e40af" : "#6b7280",
              background: "transparent",
              border: "none",
              borderBottom:
                tab === t.key ? "2px solid #1e40af" : "2px solid transparent",
              cursor: "pointer",
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "knowledge" ? (
        <ChatbotKnowledgeTab />
      ) : tab === "audit" ? (
        <ChatbotAuditTab />
      ) : (
        <div className="grid-12" style={{ gap: 18 }}>
          {/* 左:フォーム */}
          <div className="col-8">
            <Card
              title="基本設定"
              subtitle="ウィジェット名と案内文言"
              actions={
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  iconRight={<Save size={14} />}
                >
                  {saving ? "保存中..." : "保存する"}
                </Button>
              }
            >
              <div className="field">
                <label className="field-label">ウィジェット名</label>
                <input
                  className="input"
                  value={data.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  maxLength={100}
                  placeholder="例:ポータル商事サポートボット"
                />
              </div>

              <div className="field">
                <label className="field-label">チャット内表示タイトル</label>
                <input
                  className="input"
                  value={data.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  maxLength={100}
                  placeholder="空ならウィジェット名を使用"
                />
                <p className="field-hint">
                  チャットパネルのヘッダに表示されるタイトル。空の場合はウィジェット名を使用します。
                </p>
              </div>

              <div className="field">
                <label className="field-label">初回メッセージ</label>
                <textarea
                  className="textarea"
                  style={{ minHeight: 90 }}
                  value={data.welcome_message}
                  onChange={(e) => updateField("welcome_message", e.target.value)}
                  maxLength={1000}
                  placeholder="ユーザーがチャットを開いたときに最初に表示される案内文"
                />
              </div>

              <div className="field">
                <label className="field-label">入力欄プレースホルダ</label>
                <input
                  className="input"
                  value={data.placeholder}
                  onChange={(e) => updateField("placeholder", e.target.value)}
                  maxLength={100}
                />
              </div>

              <div className="field">
                <label className="field-label">
                  回答できない場合のメッセージ
                </label>
                <textarea
                  className="textarea"
                  style={{ minHeight: 90 }}
                  value={data.fallback_message}
                  onChange={(e) =>
                    updateField("fallback_message", e.target.value)
                  }
                  maxLength={1000}
                  placeholder="ナレッジにない質問への固定返答"
                />
              </div>

              <div className="field">
                <label className="field-label">回答できない場合の画像</label>
                <input
                  className="input"
                  value={data.fallback_image_url}
                  onChange={(e) => updateField("fallback_image_url", e.target.value)}
                  placeholder="Media の画像 URL、または https://..."
                />
                <p className="field-hint">
                  QR コードなど、問い合わせへ誘導する画像を表示できます。Media にアップロード済みの画像からも選択できます。
                </p>

                {data.fallback_image_url ? (
                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 12,
                      borderRadius: 14,
                      border: "1px solid rgba(148,163,184,0.28)",
                      background: "rgba(248,250,252,0.72)",
                    }}
                  >
                    <img
                      src={data.fallback_image_url}
                      alt="回答できない場合の画像プレビュー"
                      style={{
                        width: 96,
                        height: 96,
                        objectFit: "contain",
                        borderRadius: 12,
                        background: "white",
                        border: "1px solid rgba(148,163,184,0.24)",
                      }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                        プレビュー
                      </div>
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          color: "#64748b",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {data.fallback_image_url}
                      </div>
                      <button
                        type="button"
                        onClick={() => updateField("fallback_image_url", "")}
                        style={{
                          marginTop: 8,
                          border: "none",
                          background: "transparent",
                          color: "#dc2626",
                          fontWeight: 700,
                          cursor: "pointer",
                          padding: 0,
                        }}
                      >
                        画像をクリア
                      </button>
                    </div>
                  </div>
                ) : null}

                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8 }}>
                    Media から選択
                  </div>
                  {loadingMediaImages ? (
                    <div style={{ fontSize: 12, color: "#64748b" }}>画像を読み込み中...</div>
                  ) : mediaImages.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      Media に画像がありません。先にメディアページで QR 画像をアップロードしてください。
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(76px, 1fr))",
                        gap: 8,
                      }}
                    >
                      {mediaImages.map((item) => {
                        const imageUrl = item.source_url || item.thumbnail_url || "";
                        const selected = imageUrl === data.fallback_image_url;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => updateField("fallback_image_url", imageUrl)}
                            title={item.title || item.filename || String(item.id)}
                            style={{
                              aspectRatio: "1 / 1",
                              borderRadius: 12,
                              border: selected ? "2px solid #2563eb" : "1px solid rgba(148,163,184,0.30)",
                              padding: 3,
                              background: selected ? "rgba(37,99,235,0.10)" : "rgba(255,255,255,0.78)",
                              cursor: "pointer",
                              overflow: "hidden",
                            }}
                          >
                            <img
                              src={item.thumbnail_url || imageUrl}
                              alt={item.alt_text || item.title || "Media image"}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: 9,
                                display: "block",
                              }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="field">
                <label className="field-label">テーマカラー</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="color"
                    value={data.theme_color}
                    onChange={(e) => updateField("theme_color", e.target.value)}
                    style={{ width: 48, height: 32, border: "1px solid #d1d5db", borderRadius: 6 }}
                  />
                  <input
                    className="input"
                    value={data.theme_color}
                    onChange={(e) => updateField("theme_color", e.target.value)}
                    pattern="#[0-9a-fA-F]{6}"
                    placeholder="#1e40af"
                    style={{ maxWidth: 120 }}
                  />
                </div>
                <p className="field-hint">
                  ヘッダー背景・送信ボタン・起動フローティングボタンに使われます。
                </p>
              </div>

              <hr className="divider" />

              <div className="field">
                <label className="field-label">埋め込み許可ドメイン</label>
                <textarea
                  className="textarea"
                  style={{ minHeight: 70 }}
                  value={data.allowed_domains.join("\n")}
                  onChange={(e) =>
                    updateField(
                      "allowed_domains",
                      e.target.value
                        .split("\n")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    )
                  }
                  placeholder={"https://example.com\nhttps://www.example.com"}
                />
                <p className="field-hint">
                  1 行 1 ドメイン。{`localhost`} / {`127.0.0.1`} は dev デフォルトで常に許可されます。
                  最大 20 件、各 200 文字以内。
                </p>
              </div>

              <hr className="divider" />

              <div className="row-between" style={{ marginTop: 4 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                    ウィジェット稼働
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                    OFF にすると {`/session`} が 404 になり、チャットボットは表示されません。
                  </div>
                </div>
                <div
                  className={`toggle ${data.is_active ? "on" : ""}`}
                  onClick={() =>
                    updateField("is_active", !data.is_active)
                  }
                />
              </div>

              {saved && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 10,
                    borderRadius: 8,
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    color: "#15803d",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <CheckCircle2 size={14} />
                  設定を保存しました。
                </div>
              )}
            </Card>
          </div>

          {/* 右:埋め込みコードのみ(ステータス Card は demo っぽいため削除) */}
          <div className="col-4">
            <Card title="埋め込みコード" subtitle="WordPress ページに貼る script">
              {!widgetExists ? (
                // 状态 A: widget 未作成 → 显示新建按钮
                <div>
                  <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                    このテナントにはまだ chat widget が作成されていません。
                    下のボタンで作成すると、widget_key の原文が 1 回だけ表示されます。
                  </p>
                  <Button
                    onClick={handleCreateWidget}
                    disabled={creatingWidget}
                    iconLeft={<Plus size={14} />}
                  >
                    {creatingWidget ? "作成中..." : "チャットボットを作成"}
                  </Button>
                </div>
              ) : oneTimeKey ? (
                // 状态 B: widget 已在 + 本 tab 刚拿到一次性 key → 显示 + 复制
                <div className="field">
                  <label className="field-label">Widget Key(再表示不可)</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 6,
                    }}
                  >
                    <code
                      style={{
                        flex: 1,
                        padding: "6px 8px",
                        background: "#f3f4f6",
                        borderRadius: 6,
                        fontSize: 12,
                        fontFamily: "ui-monospace, monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {showKey ? oneTimeKey : "•".repeat(Math.min(oneTimeKey.length, 24))}
                    </code>
                    <button
                      onClick={() => setShowKey((s) => !s)}
                      aria-label={showKey ? "hide" : "show"}
                      style={{
                        padding: 4,
                        border: "1px solid #d1d5db",
                        background: "white",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard
                          .writeText(oneTimeKey)
                          .then(() => window.alert("Widget Key をコピーしました"))
                          .catch(() => window.alert("クリップボード拒否"));
                      }}
                      aria-label="copy"
                      style={{
                        padding: 4,
                        border: "1px solid #d1d5db",
                        background: "white",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                  <Button
                    onClick={() => handleCopyEmbed(oneTimeKey)}
                    iconRight={<Copy size={14} />}
                  >
                    スニペットをコピー
                  </Button>
                  <p
                    className="field-hint"
                    style={{ marginTop: 8, color: "#92400e" }}
                  >
                    ⚠ この Key はここ以外に表示されません。リロード前に必ずコピーしてください。
                  </p>
                </div>
              ) : (
                // 状态 C: widget 已在 + refresh 后(原文已失) → 只显示 masked + rotate-key 按钮
                <div>
                  <label className="field-label">Widget Key(マスク)</label>
                  <code
                    style={{
                      display: "block",
                      padding: "6px 8px",
                      background: "#f3f4f6",
                      borderRadius: 6,
                      fontSize: 12,
                      fontFamily: "ui-monospace, monospace",
                      marginBottom: 8,
                    }}
                  >
                    {widgetKeyMasked ?? "(unknown)"}
                  </code>
                  <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                    widget_key の原文明文は作成 / 再生成直後の 1 回しか表示されません。
                    新しいキーを発行するには下のボタンを押してください。
                  </p>
                  <Button
                    onClick={handleRotateKey}
                    disabled={rotating}
                    iconLeft={<Plus size={14} />}
                  >
                    {rotating ? "再生成中..." : "Widget Key を再生成"}
                  </Button>
                  <p
                    className="field-hint"
                    style={{ marginTop: 8, color: "#92400e" }}
                  >
                    ⚠ 再生成すると旧 key は無効化されます。既に埋め込んだページの
                    snippet も同時に更新してください。
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};
